import React, { useEffect, useState, useMemo } from 'react';
import Loader from 'react-loader-spinner';
import { FiSearch, FiCheck } from 'react-icons/fi';
import { resolveImage } from '../../utilities/infographics';
import '../../styles/editModal.css';
import '../../styles/infographics.css';

// Общая модалка выбора для инфографики: матчи, игроки, группы.
// В приложении под каждый список отдельный экран (MatchChoose, PlayerChoose,
// LeagueGroupChoose) — на сайте это одна модалка с разными загрузчиками.
//props:
// open, onClose
// title — заголовок
// loadItems — () => Promise<массив>, вызывается при открытии
// renderItem — (item) => { key, title, subtitle, image }
// multiple — множественный выбор (матчи, группы)
// selected — уже выбранное: объект или массив
// onPick — (item | массив) при подтверждении
// searchable — показать строку поиска
// emptyText — что писать, когда список пуст

export default function EntityPickerModal({
    open, onClose, title, loadItems, renderItem, multiple,
    selected, onPick, searchable, emptyText,
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [chosen, setChosen] = useState([]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setSearch('');
        setChosen(multiple ? (Array.isArray(selected) ? selected : []) : []);
        Promise.resolve(loadItems())
            .then(data => {
                if (cancelled) return;
                setItems(Array.isArray(data) ? data : (data && data.data) || []);
            })
            .catch(e => {
                if (cancelled) return;
                console.error('Ошибка загрузки списка:', e);
                setError(e);
                setItems([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // loadItems пересоздаётся на каждый рендер родителя, поэтому в зависимостях
        // только open — иначе список перезагружался бы бесконечно
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const rows = useMemo(() => {
        const mapped = items.map(item => ({ item, view: renderItem(item) }));
        const query = search.trim().toLowerCase();
        if (!query) return mapped;
        return mapped.filter(row => {
            const haystack = `${row.view.title || ''} ${row.view.subtitle || ''}`.toLowerCase();
            return haystack.indexOf(query) !== -1;
        });
    }, [items, search, renderItem]);

    if (!open) return null;

    const isChosen = (view) => chosen.some(one => renderItem(one).key === view.key);

    const toggle = (item, view) => {
        if (!multiple) {
            onPick(item);
            onClose();
            return;
        }
        setChosen(prev => (
            prev.some(one => renderItem(one).key === view.key)
                ? prev.filter(one => renderItem(one).key !== view.key)
                : prev.concat([item])
        ));
    };

    return (
        <div className="modalOverlay" onClick={onClose}>
            <div className="modalContent modalContentWide" onClick={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <h2>{title}</h2>
                    <button className="modalClose" onClick={onClose}>×</button>
                </div>

                <div className="modalBody modalBodyScroll">
                    {searchable && (
                        <div className="igSearchRow">
                            <FiSearch />
                            <input
                                type="text"
                                className="igInput"
                                value={search}
                                placeholder="Поиск"
                                onChange={(e) => setSearch(e.target.value)}
                                autoFocus
                            />
                        </div>
                    )}

                    {loading ? (
                        <div className="igLoader">
                            <Loader type="Oval" color="#04a9f5" height={44} width={44} />
                        </div>
                    ) : error ? (
                        <div className="igError">
                            {(error.response && error.response.data) || error.message || 'Не удалось загрузить список'}
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="igControlNote">{emptyText || 'Пусто.'}</p>
                    ) : rows.map(({ item, view }) => (
                        <button
                            type="button"
                            key={view.key}
                            className={`igListRow ${multiple && isChosen(view) ? 'igListRowChosen' : ''}`}
                            onClick={() => toggle(item, view)}
                        >
                            {view.image && resolveImage(view.image) ? (
                                <img src={resolveImage(view.image)} alt="" />
                            ) : null}
                            <span className="igListRowMain">
                                <span className="igListRowTitle">{view.title}</span>
                                {view.subtitle ? (
                                    <span className="igListRowMeta">{view.subtitle}</span>
                                ) : null}
                            </span>
                            {multiple && isChosen(view) ? <FiCheck className="igListRowCheck" /> : null}
                        </button>
                    ))}
                </div>

                {multiple && (
                    <div className="modalFooter">
                        <span className="igControlHint">Выбрано: {chosen.length}</span>
                        <button
                            className="igPrimaryBtn"
                            onClick={() => { onPick(chosen); onClose(); }}
                        >
                            Применить
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
